import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { authClient } from '../../lib/auth-client';
import { useBetterAuthConvexAuth } from '../../hooks/useAppAuth';
import { notePasskeyOnDevice } from '../../lib/signInSetup';
import EmailCodeSetup from './EmailCodeSetup';
import { Button, Field, Icon, Notice, headingClasses } from './AuthUi';

type SavedPasskey = { id: string; name?: string | null };

class AuthRequestError extends Error {
  status?: number;
  constructor(message: string, status?: number) { super(message); this.status = status; }
}

const Panel = ({ children }: { children: ReactNode }) =>
  <div className="space-y-3 rounded-[22px] border border-asg-line bg-white p-5 sm:p-6">{children}</div>;

/** Let an authenticated customer enrol and remove optional sign-in methods. */
export default function SignInOptions() {
  const settings = useQuery(api.passwordlessAuth.getSettings);
  const { signOut } = useBetterAuthConvexAuth();
  const [passkeys, setPasskeys] = useState<SavedPasskey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // The backend only lets a recent sign-in add a passkey or verify an email.
  const [needsFreshSignIn, setNeedsFreshSignIn] = useState(false);
  const supportsPasskey = window.isSecureContext && typeof window.PublicKeyCredential !== 'undefined';

  const listPasskeys = useCallback(async () => {
    const result = await authClient.passkey.listUserPasskeys();
    if (result.error) throw new AuthRequestError(result.error.message || 'We could not load your passkeys.', result.error.status);
    return result.data ?? [];
  }, []);

  useEffect(() => {
    listPasskeys().then(setPasskeys).catch(() => { setPasskeys([]); setError('We could not load your passkeys.'); });
  }, [listPasskeys]);

  const perform = async (operation: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    setNeedsFreshSignIn(false);
    try { await operation(); }
    catch (err) {
      if (err instanceof AuthRequestError && (err.status === 401 || err.status === 403)) setNeedsFreshSignIn(true);
      else setError(err instanceof Error && err.message ? err.message : "That didn't work. Please try again.");
    }
    finally { setBusy(false); }
  };

  const addPasskey = () => perform(async () => {
    const result = await authClient.passkey.addPasskey({ name: name.trim() || undefined });
    if (result.error) throw new AuthRequestError(result.error.message || 'Passkey setup was cancelled. You can try again.', result.error.status);
    if (!result.data) throw new Error('Passkey setup was cancelled. You can try again.');
    setName('');
    notePasskeyOnDevice(true);
    setPasskeys(await listPasskeys());
    setMessage('Passkey added. You can now sign in with it.');
  });

  const removePasskey = (passkey: SavedPasskey) => perform(async () => {
    const result = await authClient.passkey.deletePasskey({ id: passkey.id });
    if (result.error) throw new AuthRequestError(result.error.message || 'We could not remove that passkey.', result.error.status);
    const remaining = await listPasskeys();
    setPasskeys(remaining);
    if (remaining.length === 0) notePasskeyOnDevice(false);
    setMessage('Passkey removed. You can still sign in with your password.');
  });

  const signOutToRefresh = () => { void signOut().catch(() => window.location.assign('/login')); };

  return <div className="space-y-6 text-asg-ink">
    <div>
      <h2 className={headingClasses.section}>Sign-in options</h2>
      <p className="mt-1 text-[15px] text-asg-slate">Choose what works for you. Your password keeps working whatever you turn on here.</p>
    </div>

    {needsFreshSignIn && <Panel>
      <Notice tone="warning">For your security, this can only be done just after signing in. Sign out, sign back in with your password, then come straight back here.</Notice>
      <Button variant="secondary" onClick={signOutToRefresh}>Sign out</Button>
    </Panel>}
    {error && <Notice tone="danger">{error}</Notice>}
    {message && <Notice tone="success">{message}</Notice>}

    <section aria-labelledby="passkey-settings-title" className="space-y-3">
      <h3 id="passkey-settings-title" className={headingClasses.section}>Passkeys</h3>
      <p className="text-[15px] text-asg-slate">Sign in with your face, fingerprint or device PIN instead of typing a password. A passkey you add in the app works here too.</p>
      {passkeys === null ? <p role="status" className="text-[15px] text-asg-slate">Loading your passkeys…</p>
        : passkeys.length === 0 ? <p className="text-[15px] text-asg-slate">You haven't added a passkey yet.</p>
        : <ul className="divide-y divide-asg-line rounded-[22px] border border-asg-line bg-white px-5">
          {passkeys.map((passkey, index) => {
            const label = passkey.name || `Passkey ${index + 1}`;
            return <li key={passkey.id} className="flex items-center gap-4 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-asg-lime-soft text-asg-forest"><Icon name="fingerprint" /></span>
              <span className="min-w-0 flex-1 truncate text-[17px] font-semibold">{label}</span>
              <Button variant="quiet" className="!w-auto !px-3 !text-asg-danger" disabled={busy} onClick={() => void removePasskey(passkey)}>
                Remove<span className="sr-only"> {label}</span>
              </Button>
            </li>;
          })}
        </ul>}
      {supportsPasskey ? <Panel>
        <Field id="passkey-name" label="Name for this passkey" hint="Optional. Helps you tell your passkeys apart." value={name}
          onChange={event => setName(event.target.value)} maxLength={64} disabled={busy} placeholder="e.g. My laptop" />
        <Button icon="plus" loading={busy} disabled={passkeys === null} onClick={() => void addPasskey()}>Add a passkey</Button>
      </Panel> : <Notice tone="info">Passkeys can't be added in this browser. Your other sign-in options still work.</Notice>}
    </section>

    <section aria-labelledby="email-settings-title" className="space-y-3">
      <h3 id="email-settings-title" className={headingClasses.section}>Email code</h3>
      {settings === undefined ? <p role="status" className="text-[15px] text-asg-slate">Loading your sign-in email…</p>
        : settings.emailVerified && settings.email ? <Panel>
          <div>
            <p className="text-[13px] text-asg-slate">Sign-in email</p>
            <p className="text-[17px] font-semibold [overflow-wrap:anywhere]">{settings.email}</p>
          </div>
          <Notice tone="success">Email codes are turned on. Enter this email when you sign in and we'll offer to send you a code.</Notice>
        </Panel>
        : settings.email || settings.needsSignInEmail ? <Panel>
          <EmailCodeSetup variant="inline" settings={settings} onSignInAgain={signOutToRefresh} />
        </Panel>
        : <p className="text-[15px] text-asg-slate">Your account doesn't have a sign-in email we can send codes to. Contact us to check your account email, or use your password or a passkey.</p>}
    </section>
  </div>;
}
